import { toIso, toMs } from '../../domain/time';
import { newId, type Id, type Locale, type Message, type SuppressionReason } from '../../domain/types';
import type { Clock, MessageRecord, MessagingAdapter, OutboxFilter, SendRequest, StorageAdapter, Unsubscribe } from '../types';
import { renderTemplate } from './render';
import { assertSafeParams } from './templates';

/** Simulated WhatsApp latency, driven by the clock. */
export const SEND_DELAY_MS = 2_000;
export const DELIVER_DELAY_MS = 5_000;

/**
 * Mock WhatsApp: records every message in the `messages` store (the demo outbox) and moves it
 * queued -> sent -> delivered as the clock passes scheduledFor + delays. Nothing leaves the browser.
 */
export function createMockMessaging(deps: { storage: StorageAdapter; clock: Clock; locale?: () => Locale }): MessagingAdapter {
  const { storage, clock } = deps;
  const repo = storage.repo('messages');
  const locale = deps.locale ?? ((): Locale => 'he');

  async function existing(dedupeKey: string): Promise<Message | undefined> {
    const same = await repo.list({ filter: (m) => m.dedupeKey === dedupeKey && m.status !== 'cancelled' });
    return same[0];
  }

  async function create(req: SendRequest, status: Message['status'], reason?: SuppressionReason): Promise<MessageRecord> {
    assertSafeParams(req.call.template, req.call.params);
    const dup = await existing(req.dedupeKey);
    if (dup) return dup;
    const now = clock.nowIso();
    const row: Message = {
      id: newId(clock.now()), businessId: req.businessId, createdAt: now, updatedAt: now,
      channel: 'whatsapp', template: req.call.template, to: req.to, params: { ...req.call.params },
      status, scheduledFor: req.scheduledFor ?? now,
      ...(reason ? { suppressionReason: reason } : req.deferred ? { suppressionReason: 'quiet_hours_deferred' as const } : {}),
      ...(req.appointmentId ? { appointmentId: req.appointmentId } : {}),
      clientId: req.clientId, purpose: req.purpose, dedupeKey: req.dedupeKey,
      text: renderTemplate(req.call, locale()), mock: true,
    };
    return repo.put(row);
  }

  async function patch(id: Id, change: Partial<Message>): Promise<void> {
    const cur = await repo.get(id);
    if (cur) await repo.put({ ...cur, ...change, updatedAt: clock.nowIso() });
  }

  return {
    label: 'WhatsApp (demo)',
    send: (req) => create(req, 'queued'),
    suppress: (req, reason) => create(req, 'suppressed', reason),
    async cancel(id) {
      const cur = await repo.get(id);
      if (cur && (cur.status === 'queued' || (cur.status === 'suppressed' && cur.suppressionReason === 'plan_free_manual'))) {
        await patch(id, { status: 'cancelled' });
      }
    },
    async reschedule(id, scheduledFor, reason) {
      await patch(id, { scheduledFor, ...(reason ? { suppressionReason: reason } : {}) });
    },
    async markSuppressed(id, reason) {
      await patch(id, { status: 'suppressed', suppressionReason: reason });
    },
    async markManualSent(id) {
      await patch(id, { status: 'sent', sentAt: clock.nowIso(), manual: true });
    },
    async listOutbox(filter: OutboxFilter = {}) {
      const { businessId, ...rest } = filter;
      const rows = await repo.list({ where: { ...(businessId ? { businessId } : {}), ...rest } });
      return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
    },
    onStatus(cb): Unsubscribe {
      return storage.subscribe('messages', (e) => {
        if (e.type !== 'put' || !e.id) return;
        void repo.get(e.id).then((m) => { if (m) cb(m); });
      });
    },
    async dispatchNow(id) {
      const cur = await repo.get(id);
      if (!cur || cur.status !== 'queued' || toMs(cur.scheduledFor) > clock.now()) return cur;
      return repo.put({ ...cur, status: 'sent', sentAt: clock.nowIso(), updatedAt: clock.nowIso() });
    },
    async tick() {
      const now = clock.now();
      let changed = 0;
      for (const m of await repo.list({ where: { status: 'queued' } })) {
        const sentAt = toMs(m.scheduledFor) + SEND_DELAY_MS;
        if (sentAt > now) continue;
        await repo.put({ ...m, status: 'sent', sentAt: toIso(sentAt), updatedAt: clock.nowIso() });
        changed++;
      }
      for (const m of await repo.list({ where: { status: 'sent' } })) {
        if (!m.sentAt || m.manual) continue;
        const at = toMs(m.sentAt) + DELIVER_DELAY_MS;
        if (at > now) continue;
        await repo.put({ ...m, status: 'delivered', deliveredAt: toIso(at), updatedAt: clock.nowIso() });
        changed++;
      }
      return changed;
    },
  };
}
